import * as React from 'react';
import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField } from '@mui/material';
import { createVirtualLeague, previewVirtualLeague } from '../../Api';
import { VirtualProviderLeagueDto, VirtualLeagueDraftDto } from '../../models/dtos/VirtualProviderCatalogDto';
import { VIRTUAL_FOOTBALL_ID, VirtualCatalogSportDto } from '../../models/dtos/VirtualSportsCatalogDto';

const EMPTY_DRAFT: VirtualLeagueDraftDto = {
    name: '',
    nameRu: '',
    description: '',
    descriptionRu: '',
    leagueType: 'DOMESTIC',
    speed: 'MEDIUM',
    seasonIntervalHours: 0,
};

const TEXT_FIELDS = ['name', 'nameRu', 'description', 'descriptionRu'] as const;

export default function VirtualLeagueCreationDialog(props: {
    open: boolean;
    sports: VirtualCatalogSportDto[];
    onClose: () => void;
    onCreated: (league: VirtualProviderLeagueDto) => void;
}) {
    const [sportId, setSportId] = React.useState<number | ''>('');
    const [draft, setDraft] = React.useState(EMPTY_DRAFT);
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState('');

    React.useEffect(() => {
        if (props.open) {
            setSportId(props.sports.some(sport => sport.id === VIRTUAL_FOOTBALL_ID) ? VIRTUAL_FOOTBALL_ID : '');
            setDraft(EMPTY_DRAFT);
            setError('');
        }
    }, [props.open, props.sports]);

    const update = (field: typeof TEXT_FIELDS[number] | 'leagueType' | 'speed', value: string) =>
        setDraft(current => ({ ...current, [field]: value }));
    const updateSeasonIntervalHours = (value: number) =>
        setDraft(current => ({ ...current, seasonIntervalHours: Number.isFinite(value) ? Math.max(0, value) : 0 }));
    const run = async (action: () => Promise<void>) => {
        setBusy(true);
        setError('');
        try { await action(); } catch (reason) {
            setError(reason instanceof Error ? reason.message : 'Request failed');
        } finally { setBusy(false); }
    };
    const valid = sportId !== '' && TEXT_FIELDS.every(field => draft[field].trim().length >= 3);

    return (
        <Dialog open={props.open} onClose={busy ? undefined : props.onClose} fullWidth maxWidth="md">
            <DialogTitle>Add virtual league</DialogTitle>
            <DialogContent>
                {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField select required label="Virtual sport" value={sportId} onChange={event => setSportId(Number(event.target.value))}>
                        {props.sports.map(sport => <MenuItem key={sport.id} value={sport.id}>{sport.name}</MenuItem>)}
                    </TextField>
                    <TextField required label="Name (English)" value={draft.name} onChange={e => update('name', e.target.value)} />
                    <TextField required label="Name (Russian)" value={draft.nameRu} onChange={e => update('nameRu', e.target.value)} />
                    <TextField required multiline minRows={4} label="Description (English)" value={draft.description} onChange={e => update('description', e.target.value)} />
                    <TextField required multiline minRows={4} label="Description (Russian)" value={draft.descriptionRu} onChange={e => update('descriptionRu', e.target.value)} />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <TextField select fullWidth required label="Competition type" value={draft.leagueType} onChange={e => update('leagueType', e.target.value)}>
                            <MenuItem value="DOMESTIC">Domestic · fixed round-robin</MenuItem>
                            <MenuItem value="PLAYOFF">Playoff · dynamic bracket</MenuItem>
                            <MenuItem value="FRIENDLY">Friendly · seasonless training matches</MenuItem>
                        </TextField>
                        <TextField select fullWidth required label="Fixture speed" value={draft.speed} onChange={e => update('speed', e.target.value)}>
                            <MenuItem value="IMMEDIATE">Immediate · 15 minutes</MenuItem>
                            <MenuItem value="QUICK">Quick · 1 hour</MenuItem>
                            <MenuItem value="MEDIUM">Medium · 6 hours</MenuItem>
                            <MenuItem value="SLOW">Slow · 12 hours</MenuItem>
                            <MenuItem value="REALISTIC">Realistic · 1 day</MenuItem>
                        </TextField>
                        <TextField
                            fullWidth
                            type="number"
                            label="Season interval (hours)"
                            helperText="Gap kept after a season completes before the next one is scheduled"
                            value={draft.seasonIntervalHours}
                            onChange={e => updateSeasonIntervalHours(Number(e.target.value))}
                            inputProps={{ min: 0 }}
                        />
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={props.onClose} disabled={busy}>Cancel</Button>
                <Button
                    variant="outlined"
                    disabled={sportId === '' || busy}
                    onClick={() => run(async () => setDraft(await previewVirtualLeague(sportId as number)))}
                >
                    {busy ? <CircularProgress size={20} /> : 'Prepopulate with AI'}
                </Button>
                <Button
                    variant="contained"
                    disabled={!valid || busy}
                    onClick={() => run(async () => {
                        const created = await createVirtualLeague(sportId as number, draft);
                        props.onCreated(created);
                        props.onClose();
                    })}
                >Save</Button>
            </DialogActions>
        </Dialog>
    );
}
